import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, switchMap} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SocketNamespaceServiceBase} from '~local/app/core/socket/SocketNamespaceServiceBase';
import {SearchListType, SearchTicketsType, TicketAttachmentEntity, TicketDto, TicketSimpleDto} from '~shared/types/ticket.types';
import {DialogService} from '~shared/ng/services/dialog/dialog.service';
import {SimpleModalType} from '~shared/types/dialog.types';
import {downloadFile} from '~shared/utils/utils';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthenticationService} from '~local/app/core/services/authentication.service';
import {SOMETHING_WRONG} from '~local/app/shared/constant/errors';

type TicketState = {
    tickets: Array<TicketSimpleDto>
};

type UnreadTicketsState = TicketState & {
    unreadCount: number,
};

type UpdateTicketData = { ticket: TicketSimpleDto, unreadCount: number };

@Injectable()
export class TicketService extends SocketNamespaceServiceBase {
    private readonly httpClient = inject(HttpClient);
    private readonly dialogService = inject(DialogService);
    private readonly authenticationService = inject(AuthenticationService);

    // should be not more than 10 in view
    private readonly _unreadTicketsState$ = new BehaviorSubject<UnreadTicketsState | null>(null);
    private readonly _searchTicketsState$ = new BehaviorSubject<Array<SearchTicketsType>>([]);
    private readonly _updateTicketSubject$ = new BehaviorSubject<TicketDto | null>(null);
    private readonly _ticketsState$ = new BehaviorSubject<Array<TicketSimpleDto>>([]);
    private preventListLoad: boolean = false;

    public constructor() {
        super('tickets');
        this.authenticationService.credentials$.pipe(
            filter($ => $ === null),
            takeUntilDestroyed()
        ).subscribe(() => {
            this._unreadTicketsState$.next(null);
            this._ticketsState$.next([]);
            this._searchTicketsState$.next([]);
            this.preventListLoad = false;
        });
    }

    public create(formData: FormData): Observable<{ status: 'success' }> {
        return this.httpClient.post<{ status: 'success' }>('/tickets', formData);
    }

    public getTickets(): Observable<Array<TicketSimpleDto>> {
        // preventListLoad orient on "take" parameter
        if (this.preventListLoad) {
            return of([]);
        }

        const params = new HttpParams()
            .set('skip', this._ticketsState$.value.length)
            .set('take', 20);

        return this.httpClient.get<Array<TicketSimpleDto>>('/tickets/list', {params}).pipe(
            tap({
                next: ($data) => {
                    this.preventListLoad = $data.length < 20;
                    this._ticketsState$.next([...this._ticketsState$.value, ...$data]);
                },
                error: ($error) => {
                    this.dialogService.createSimple({
                        type: SimpleModalType.ERROR,
                        content: $error.error?.errors[0]?.error ?? SOMETHING_WRONG
                    });
                }
            })
        );
    }

    public getUnreadTickets(skip: number = 0, take: number = 10): Observable<Array<TicketSimpleDto>> {
        const params = new HttpParams().set('skip', skip).set('take', take);
        return this.httpClient.get<Array<TicketSimpleDto>>('/tickets/unread', {params});
    }

    public searchByMessage(message: string): Observable<Array<SearchTicketsType>> {
        if (!message) {
            this._searchTicketsState$.next([]);
            return of([]);
        }
        const params = new HttpParams().set('message', message);
        return this.httpClient.get<Array<SearchTicketsType>>('/tickets/search', {params})
            .pipe(
                tap({
                    next: ($data) => {
                        this._searchTicketsState$.next($data);
                    },
                    error: () => {
                        this._searchTicketsState$.next([]);
                    }
                }),
            );
    }

    public getDetail$(id: number): Observable<TicketDto> {
        return this.httpClient.get<TicketDto>('/tickets/' + id).pipe(
            tap({
                next: ($ticketDetail) => {
                    this._updateTicketSubject$.next($ticketDetail);
                },
                error: (error) => {
                    this.dialogService.createSimple({
                        type: SimpleModalType.ERROR,
                        content: error.error?.errors[0]?.error ?? SOMETHING_WRONG
                    });
                }
            }),
            switchMap((_) => {
                return this._updateTicketSubject$;
            })
        );
    }

    public sendTicketMessage(formData: FormData): Observable<TicketDto> {
        return this.httpClient.post<TicketDto>('/tickets/replay', formData).pipe(
            tap(($ticketDetail) => {
                    this._updateTicketSubject$.next($ticketDetail);
                }
            ),
        );
    }

    public downloadAttachment(ticketId: number, attachment: TicketAttachmentEntity) {
        try {
            this.httpClient.get('/tickets/attachment/' + ticketId + '/' + attachment.key, {responseType: 'blob'})
                .subscribe(fileBlob => {
                    downloadFile(fileBlob, attachment.name);
                });
        } catch (error) {
            this.dialogService.createSimple({
                type: SimpleModalType.ERROR,
                content: error.error?.errors[0]?.error ?? SOMETHING_WRONG
            });
        }
    }

    protected onConnect() {
        this.initUnreadTicketsState();
    }

    protected initUnreadTicketsState() {
        // initial unread state
        this.socket.once('dataR', ($data: UnreadTicketsState) => {
            this._unreadTicketsState$.next({
                unreadCount: $data.unreadCount,
                tickets: $data.tickets,
            });
        });
        this.socket.emit('data');
    }

    protected initListeners(): void {
        this.socket.on('update', ($data: UpdateTicketData) => {
            const ticketDetail = this._updateTicketSubject$.value;
            if (ticketDetail && ticketDetail.id === $data.ticket.id && !$data.ticket.isRead) {
                this._updateTicketSubject$.next(
                    {
                        ...ticketDetail,
                        ...$data.ticket,
                        ticketMessages: [...ticketDetail.ticketMessages, $data.ticket.lastMessage]
                    }
                );
            }
            this.updateUnreadTicketsState($data);
            this.updateTicketState($data.ticket);
            this.updateSearchTicketState($data.ticket);
        });

        this.socket.on('new', ($data: UpdateTicketData) => {
            if (this._ticketsState$.value.length) {
                this._ticketsState$.next([$data.ticket, ...this._ticketsState$.value]);
            }

            if (!$data.ticket.isRead) {
                this._unreadTicketsState$.next({
                    unreadCount: $data.unreadCount,
                    tickets: [$data.ticket, ...this._unreadTicketsState$.value.tickets],
                });
            }
        });
    }

    private updateUnreadTicketsState($data: UpdateTicketData): void {
        const tickets = [...this._unreadTicketsState$.value.tickets];
        const existingIndex = tickets.findIndex($ => $.id === $data.ticket.id);
        if (existingIndex > -1) {

            if ($data.ticket.isRead) {
                // remove read ticket from unread list and add elder one
                if ($data.unreadCount >= 10) {
                    this.getUnreadTickets(9, 1).subscribe(($tickets) => {
                        this._unreadTicketsState$.next({
                            unreadCount: $data.unreadCount,
                            tickets: [...tickets.slice(0, existingIndex), ...tickets.slice(existingIndex + 1), ...$tickets],
                        });
                    });
                    return;
                }

                // just remove read ticket from unread list
                this._unreadTicketsState$.next({
                    unreadCount: $data.unreadCount,
                    tickets: [...tickets.slice(0, existingIndex), ...tickets.slice(existingIndex + 1)],
                });
                return;
            }

            // Update unread ticket list
            this._unreadTicketsState$.next({
                unreadCount: $data.unreadCount,
                tickets: [$data.ticket, ...tickets.slice(0, existingIndex), ...tickets.slice(existingIndex + 1)],
            });

        } else {

            if (!$data.ticket.isRead) {
                const list = $data.unreadCount > 10 ? tickets.slice(0, -1) : tickets;
                this._unreadTicketsState$.next({
                    unreadCount: $data.unreadCount,
                    tickets: [$data.ticket, ...list],
                });
            }
        }
    }

    private updateTicketState($ticket: TicketSimpleDto): void {
        if (this._ticketsState$.value.length) {
            const tickets = this._ticketsState$.value;
            const existingIndex = tickets.findIndex($ => $.id === $ticket.id);
            if (existingIndex > -1) {
                this._ticketsState$.next([$ticket, ...tickets.slice(0, existingIndex), ...tickets.slice(existingIndex + 1)]);
                return;
            }
            this._ticketsState$.next([$ticket, ...tickets]);
        }
    }

    private updateSearchTicketState($ticket: TicketSimpleDto): void {
        const state = this._searchTicketsState$.value;
        if (state.length) {
            const topicListIndex = state.findIndex(($) => $.type === SearchListType.TOPIC_LIST);
            if (topicListIndex > -1) {
                const list = state[topicListIndex].list;
                const existingIndex = list.findIndex($ => $.id === $ticket.id);
                if (existingIndex > -1) {
                    state[topicListIndex].list = [$ticket, ...list.slice(0, existingIndex), ...list.slice(existingIndex + 1)];
                    this._searchTicketsState$.next(state);
                }
            }
        }
    }

    public get unreadTicketsCount$(): Observable<number> {
        return this._unreadTicketsState$.pipe(
            filter($ => $ !== null),
            map($ => $.unreadCount),
        );
    }

    public get unreadTickets$(): Observable<Array<TicketSimpleDto>> {
        return this._unreadTicketsState$.pipe(
            filter($ => $ !== null),
            map($ => $.tickets),
        );
    }

    public get ticketsState$(): Observable<Array<TicketSimpleDto>> {
        return this._ticketsState$;
    }

    public get searchTicketsState$(): Observable<Array<SearchTicketsType>> {
        return this._searchTicketsState$;
    }

    public get ticketsState(): Array<TicketSimpleDto> {
        return this._ticketsState$.value;
    }

}
